import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {
  FiArrowLeft, FiUser, FiMail, FiBriefcase, FiGlobe,
  FiClock, FiMessageSquare, FiCheck, FiLoader, FiAlertCircle,
  FiLink, FiFileText, FiCalendar, FiMapPin, FiMusic
} = FiIcons;

const SUBMISSIONS_EMAIL = 'submissions@visionmedia345.com';

const buildEmailBody = (d) => [
  'NEW PLATFORM ADMIN APPLICATION — Vision Direct',
  '',
  `Full Name:         ${d.display_name || 'N/A'}`,
  `Email:             ${d.email || 'N/A'}`,
  `Current Role:      ${d.current_job_role || 'N/A'}`,
  `Organization:      ${d.company_organization || 'N/A'}`,
  `Experience:        ${d.years_experience ? `${d.years_experience} years` : 'N/A'}`,
  `Availability:      ${d.availability || 'N/A'}`,
  `Timezone:          ${d.timezone || 'N/A'}`,
  '',
  `LinkedIn:          ${d.linkedin_url || 'Not provided'}`,
  `Website:           ${d.website || 'Not provided'}`,
  '',
  '--- Why Platform Admin? ---',
  d.reason_for_access || 'Not provided',
  '',
  '--- Platform Vision ---',
  d.platform_vision || 'Not provided',
  '',
  '--- Expected Usage ---',
  d.expected_usage || 'Not provided',
  '',
  `Bio: ${d.bio || 'Not provided'}`,
  d.social_instagram ? `Instagram: ${d.social_instagram}` : '',
  d.social_spotify   ? `Spotify:   ${d.social_spotify}`   : '',
  '',
  'Submitted via Vision Direct Platform',
].filter(l => l !== null && l !== undefined).join('\n');

// Called SYNCHRONOUSLY inside the click handler — before any await
const triggerEmail = (formData) => {
  try {
    const subject = encodeURIComponent(
      `[Platform Admin Application] ${formData.display_name || formData.email}`
    );
    const body = encodeURIComponent(buildEmailBody(formData));
    const a = document.createElement('a');
    a.href = `mailto:${SUBMISSIONS_EMAIL}?subject=${subject}&body=${body}`;
    a.target = '_blank';
    a.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch (_) {} }, 1000);
  } catch (err) {
    console.error('Email trigger failed:', err);
  }
};

const PlatformAdminForm = ({ user, onSubmit, loading, error }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    display_name: '',
    bio: '',
    current_job_role: '',
    company_organization: '',
    years_experience: '',
    linkedin_url: '',
    reason_for_access: '',
    platform_vision: '',
    expected_usage: '',
    availability: 'part-time',
    timezone: '',
    portfolio_url: '',
    website: '',
    social_spotify: '',
    social_instagram: '',
    social_soundcloud: '',
  });
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // onClick handler — NOT async — so email fires within user gesture
  const handleSubmitClick = (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!formData.display_name.trim()) {
      setSubmitError('Please enter your full name');
      return;
    }
    if (!formData.email.trim()) {
      setSubmitError('Please enter your email address');
      return;
    }
    if (!formData.reason_for_access.trim()) {
      setSubmitError('Please explain why you want platform admin access');
      return;
    }

    // ⚡ Fire email SYNCHRONOUSLY — still within the user gesture stack
    triggerEmail(formData);

    // Now hand off to the async parent handler
    onSubmit(formData).catch(err => {
      setSubmitError(err.message || 'Failed to submit application');
    });
  };

  const nextStep = () => {
    if (step === 1 && !formData.display_name.trim()) { setSubmitError('Please enter your full name'); return; }
    if (step === 1 && !formData.email.trim()) { setSubmitError('Please enter your email address'); return; }
    if (step === 2 && !formData.reason_for_access.trim()) { setSubmitError('Please explain your reason for access'); return; }
    setSubmitError('');
    setStep(step + 1);
  };

  const prevStep = () => setStep(step - 1);

  return (
    <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center px-6 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink w-[600px] h-[600px] top-[-20%] right-[-15%] animate-caustic" />
        <div className="ambient-orb-coral w-[500px] h-[500px] bottom-[-15%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 w-full max-w-3xl space-y-6">
        <button onClick={() => navigate('/')}
          className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group w-fit">
          <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
            <SafeIcon icon={FiArrowLeft} className="text-sm" />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Home</span>
        </button>

        <div className="glass-strong rounded-[2.5rem] p-10 space-y-8">
          <div className="text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center mx-auto">
              <SafeIcon icon={FiBriefcase} className="text-3xl text-red-400" />
            </div>
            <h1 className="text-2xl font-black uppercase tracking-tighter text-white">Platform Admin Application</h1>
            <p className="text-zinc-500 font-bold text-sm">Join the Vision Direct leadership team</p>
          </div>

          <div className="flex items-center gap-3">
            {[1, 2, 3, 4].map(s => (
              <div key={s} className="flex-1 flex items-center gap-2">
                <div className={`flex-1 h-2 rounded-full ${step >= s ? 'bg-red-500' : 'bg-zinc-800'}`} />
                <span className={`text-[9px] font-black uppercase tracking-widest whitespace-nowrap ${step >= s ? 'text-red-400' : 'text-zinc-600'}`}>
                  {s === 1 ? 'Info' : s === 2 ? 'Vision' : s === 3 ? 'Background' : 'Details'}
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Email Address *</label>
                  <div className="relative">
                    <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required
                      placeholder="you@example.com"
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Full Name *</label>
                  <div className="relative">
                    <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                    <input type="text" name="display_name" value={formData.display_name} onChange={handleChange} required
                      placeholder="Your full legal name"
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Professional Bio</label>
                  <textarea name="bio" value={formData.bio} onChange={handleChange}
                    placeholder="Brief overview of your professional background..."
                    rows={4}
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl p-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all resize-none" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiMessageSquare} className="text-sm" /> Why Platform Admin? *
                  </label>
                  <textarea name="reason_for_access" value={formData.reason_for_access} onChange={handleChange} required
                    placeholder="Explain why you want platform admin access and what you hope to achieve..."
                    rows={5}
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl p-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all resize-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiGlobe} className="text-sm" /> Platform Vision
                  </label>
                  <textarea name="platform_vision" value={formData.platform_vision} onChange={handleChange}
                    placeholder="How do you envision using and improving the platform?"
                    rows={4}
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl p-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all resize-none" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiFileText} className="text-sm" /> Expected Usage
                  </label>
                  <textarea name="expected_usage" value={formData.expected_usage} onChange={handleChange}
                    placeholder="Describe your expected responsibilities and usage patterns..."
                    rows={4}
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl p-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all resize-none" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Current Role</label>
                  <div className="relative">
                    <SafeIcon icon={FiBriefcase} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600 text-sm" />
                    <input type="text" name="current_job_role" value={formData.current_job_role} onChange={handleChange}
                      placeholder="e.g., Product Manager, CTO, Operations Director"
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 pl-11 pr-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Company/Organization</label>
                  <input type="text" name="company_organization" value={formData.company_organization} onChange={handleChange}
                    placeholder="Current or most recent organization"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500">Years of Experience</label>
                  <input type="number" name="years_experience" value={formData.years_experience} onChange={handleChange}
                    placeholder="e.g., 5" min="0" max="50"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiLink} className="text-sm" /> LinkedIn Profile
                  </label>
                  <input type="url" name="linkedin_url" value={formData.linkedin_url} onChange={handleChange}
                    placeholder="https://linkedin.com/in/yourname"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                </div>
              </div>
            )}

            {step === 4 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                      <SafeIcon icon={FiClock} className="text-sm" /> Availability
                    </label>
                    <select name="availability" value={formData.availability} onChange={handleChange}
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white focus:border-red-500/40 focus:outline-none transition-all appearance-none cursor-pointer">
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contractor">Contractor</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                      <SafeIcon icon={FiMapPin} className="text-sm" /> Timezone
                    </label>
                    <input type="text" name="timezone" value={formData.timezone} onChange={handleChange}
                      placeholder="e.g., PST, EST, GMT"
                      className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiLink} className="text-sm" /> Portfolio/Website
                  </label>
                  <input type="url" name="website" value={formData.website} onChange={handleChange}
                    placeholder="https://yourportfolio.com"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiMusic} className="text-sm" /> Spotify (Optional)
                  </label>
                  <input type="url" name="social_spotify" value={formData.social_spotify} onChange={handleChange}
                    placeholder="https://open.spotify.com/artist/..."
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 flex items-center gap-2">
                    <SafeIcon icon={FiCalendar} className="text-sm" /> Instagram (Optional)
                  </label>
                  <input type="url" name="social_instagram" value={formData.social_instagram} onChange={handleChange}
                    placeholder="https://instagram.com/yourname"
                    className="w-full bg-zinc-900/60 border border-white/8 rounded-2xl py-4 px-4 text-sm font-bold text-white placeholder-zinc-700 focus:border-red-500/40 focus:outline-none transition-all" />
                </div>
              </div>
            )}

            {(error || submitError) && (
              <div className="flex items-start gap-3 p-4 bg-red-500/8 border border-red-500/20 rounded-2xl">
                <SafeIcon icon={FiAlertCircle} className="text-red-400 text-sm flex-shrink-0 mt-0.5" />
                <p className="text-xs font-bold text-red-400">{error || submitError}</p>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              {step > 1 && (
                <button type="button" onClick={prevStep}
                  className="flex-1 py-4 glass-card rounded-2xl text-xs font-black uppercase tracking-widest text-zinc-400 hover:text-white transition-all">
                  Back
                </button>
              )}
              {step < 4 ? (
                <button type="button" onClick={nextStep}
                  className="flex-1 py-4 bg-red-500 hover:bg-red-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest transition-all">
                  Continue
                </button>
              ) : (
                <button type="button" onClick={handleSubmitClick} disabled={loading}
                  className="flex-1 py-4 bg-red-500 hover:bg-red-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {loading
                    ? <><SafeIcon icon={FiLoader} className="animate-spin text-base" /> Submitting…</>
                    : <><SafeIcon icon={FiCheck} /> Submit Application</>
                  }
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformAdminForm;